package com.ijoin.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier

/**
 * Cross-platform component for rendering HTML content.
 * Used to display custom landing HTML for events.
 *
 * The HTML can include:
 * - Embedded CSS via <style> tags
 * - Base64 encoded images via data: URLs
 * - Standard HTML elements
 *
 * ## Navigation Buttons Placement (Reserved Area)
 *
 * The app automatically displays navigation buttons over the landing content:
 * - **Back button** (←): Top-left corner, 16px from edges
 * - **Book button** + **Share button**: Top-right corner, 16px from edges
 *
 * **IMPORTANT for landing HTML authors:**
 * Avoid placing important content or interactive elements in the top 72px
 * of your landing page, as this area is reserved for navigation buttons.
 *
 * Reserved zones (from top):
 * ```
 * ┌─────────────────────────────────────────┐
 * │ [←]                      [Book] [Share] │ ← Top 72px reserved
 * ├─────────────────────────────────────────┤
 * │                                         │
 * │         Your landing content            │
 * │         (safe area below)               │
 * │                                         │
 * └─────────────────────────────────────────┘
 * ```
 *
 * Recommended: Add `padding-top: 72px` to your landing body or first section.
 *
 * ## Form Integration
 *
 * Landing pages can submit form data using the injected `IJOIN` helper:
 *
 * ```html
 * <form onsubmit="return IJOIN.submitForm(this)">
 *   <input name="name" type="text" required/>
 *   <input name="phone" type="tel"/>
 *   <textarea name="message"></textarea>
 *   <button type="submit">Send</button>
 * </form>
 * ```
 *
 * Or with custom data:
 * ```javascript
 * IJOIN.submitForm({
 *   name: 'John',
 *   phone: '+7999123456',
 *   message: 'Hello'
 * });
 * ```
 *
 * @param html The HTML content to render
 * @param modifier Modifier for the component
 * @param eventId The event ID for form submissions
 * @param onHeightChanged Callback when content height changes (for dynamic sizing)
 * @param showHeader Whether to show the floating header with navigation buttons
 * @param onBackClick Callback when back button is clicked
 * @param onBookClick Callback when book button is clicked
 * @param onShareClick Callback when share button is clicked
 * @param bookButtonText Text for the book button
 * @param onFormSubmit Callback when form is submitted from landing (receives form data as Map)
 */
@Composable
expect fun HtmlLandingView(
    html: String,
    modifier: Modifier = Modifier,
    eventId: String? = null,
    onHeightChanged: ((Int) -> Unit)? = null,
    showHeader: Boolean = false,
    onBackClick: (() -> Unit)? = null,
    onBookClick: (() -> Unit)? = null,
    onShareClick: (() -> Unit)? = null,
    bookButtonText: String? = null,
    onFormSubmit: ((Map<String, Any>) -> Unit)? = null
)

/**
 * Wraps user HTML with base styles for consistent rendering.
 * @param html The user's HTML content
 * @param eventId Optional event ID for form submissions
 */
fun wrapHtmlWithBaseStyles(html: String, eventId: String? = null): String {
    val safeEventId = eventId?.replace("'", "\\'") ?: ""
    return """
        <!DOCTYPE html>
        <html>
        <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no">
            <style>
                * {
                    box-sizing: border-box;
                    -webkit-tap-highlight-color: transparent;
                }
                html, body {
                    margin: 0;
                    padding: 0;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
                    font-size: 16px;
                    line-height: 1.5;
                    color: #1a1a1a;
                    background: transparent;
                    overflow-x: hidden;
                }
                img {
                    max-width: 100%;
                    height: auto;
                }
                a {
                    color: #6C5CE7;
                }
            </style>
        </head>
        <body>
            $html
            <script>
                // IJOIN Form Integration Helper
                window.IJOIN = {
                    eventId: '$safeEventId',

                    // Submit form data to parent app
                    // Usage: <form onsubmit="return IJOIN.submitForm(this)">
                    // Or: IJOIN.submitForm({name: 'John', phone: '+7999'})
                    submitForm: function(formOrData) {
                        var data = {};

                        if (formOrData instanceof HTMLFormElement) {
                            // Extract data from form element
                            var formData = new FormData(formOrData);
                            formData.forEach(function(value, key) {
                                data[key] = value;
                            });
                        } else if (typeof formOrData === 'object') {
                            // Use object directly
                            data = formOrData;
                        }

                        // Try native bridge first (Android/iOS), then postMessage (Web)
                        if (window.IjoinBridge && window.IjoinBridge.onFormSubmit) {
                            // Android native bridge
                            window.IjoinBridge.onFormSubmit(JSON.stringify(data));
                        } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.IjoinBridge) {
                            // iOS native bridge
                            window.webkit.messageHandlers.IjoinBridge.postMessage({type: 'formSubmit', data: data});
                        } else {
                            // Web (iframe) - use postMessage
                            window.parent.postMessage({
                                type: 'IJOIN_FORM_SUBMIT',
                                eventId: this.eventId,
                                formData: data
                            }, '*');
                        }

                        return false; // Prevent default form submission
                    },

                    // Show success message (can be customized)
                    showSuccess: function(message) {
                        alert(message || 'Submit!');
                    },

                    // Show error message
                    showError: function(message) {
                        alert(message || 'Error submit');
                    }
                };

                // Handle anchor links (scroll to element)
                document.addEventListener('click', function(e) {
                    var target = e.target.closest('a[href^="#"]');
                    if (target) {
                        e.preventDefault();
                        var hash = target.getAttribute('href');
                        if (hash && hash.length > 1) {
                            var elementId = hash.substring(1);
                            var element = document.getElementById(elementId);
                            if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                        }
                    }
                });

                // Report height changes to native (for mobile)
                function reportHeight() {
                    var height = document.body.scrollHeight;
                    if (window.HtmlBridge && window.HtmlBridge.onHeightChanged) {
                        window.HtmlBridge.onHeightChanged(height);
                    }
                    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.HtmlBridge) {
                        window.webkit.messageHandlers.HtmlBridge.postMessage(height);
                    }
                }
                window.onload = reportHeight;
                new ResizeObserver(reportHeight).observe(document.body);
            </script>
        </body>
        </html>
    """.trimIndent()
}
